import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Link,
  TextField,
  Typography,
} from '@mui/material';
import {
  ArrowBack,
  EmailOutlined,
  LockOutlined,
  PersonOutline,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { fieldSx, submitSx } from './LoginPage';
import { useAuth } from './context/AuthContext';

const SignUpPage: React.FC = () => {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const { register } = useAuth();
  const navigate = useNavigate();

  const isValid = username.trim() !== '' && email.trim() !== '' && password.length >= 1 && confirmPassword.length >= 1;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid || isSubmitting) return;
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setIsSubmitting(true);

    try {
      await register(username, email, password);
      navigate('/login', {
        replace: true,
        state: { message: 'Account created successfully. Please sign in.' },
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex' }}>
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          flex: 1,
          flexDirection: 'column',
          justifyContent: 'space-between',
          px: 7,
          py: 6,
          background: 'linear-gradient(160deg, #0a1628 0%, #102040 60%, #0d2550 100%)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ position: 'absolute', inset: 0, opacity: 0.22, background: 'linear-gradient(135deg, transparent 0%, rgba(96,165,250,0.45) 100%)' }} />
        <Typography sx={{ fontSize: '0.8rem', fontWeight: 800, color: 'white', letterSpacing: '4px', textTransform: 'uppercase', zIndex: 1 }}>
          SeeVita
        </Typography>
        <Box sx={{ zIndex: 1 }}>
          <Typography sx={{ fontSize: '3rem', fontWeight: 800, color: 'white', lineHeight: 1.1, mb: 2.5 }}>
            Your health,
            <br />
            <Box component="span" sx={{ background: 'linear-gradient(90deg, #60a5fa, #a78bfa)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              Start your journey.
            </Box>
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.95rem', maxWidth: 320, lineHeight: 1.7 }}>
            Secure access for AI-powered vital sign monitoring.
          </Typography>
        </Box>
        <Typography sx={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.78rem', zIndex: 1 }}>
          Copyright 2026 SeeVita. All rights reserved.
        </Typography>
      </Box>

      <Box
        sx={{
          flex: { xs: 1, md: '0 0 480px' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: { xs: 3, sm: 6 },
          py: 6,
          bgcolor: '#ffffff',
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 360 }}>
          <Typography sx={{ display: { xs: 'block', md: 'none' }, fontSize: '0.75rem', fontWeight: 800, color: '#0a1628', letterSpacing: '4px', textTransform: 'uppercase', mb: 5 }}>
            SeeVita
          </Typography>

          <Button
            component={RouterLink}
            to="/login"
            startIcon={<ArrowBack />}
            sx={{ mb: 2, color: '#64748b', textTransform: 'none', fontWeight: 700, px: 0 }}
          >
            Back to sign in
          </Button>

          <Typography sx={{ fontSize: '1.85rem', fontWeight: 800, color: '#0a1628', mb: 0.8 }}>
            Create account
          </Typography>
          <Typography sx={{ color: '#64748b', fontSize: '0.88rem', mb: 4 }}>
            Set up secure access to your health dashboard.
          </Typography>

          {error && <Alert severity="error" sx={{ mb: 3, borderRadius: '12px', fontSize: '0.85rem' }}>{error}</Alert>}

          <Box component="form" onSubmit={handleSubmit} noValidate sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Username"
              fullWidth
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled={isSubmitting}
              required
              autoComplete="username"
              autoFocus
              InputProps={{ startAdornment: <FieldIcon icon={<PersonOutline />} active={Boolean(username)} /> }}
              sx={fieldSx}
            />

            <TextField
              label="Email address"
              type="email"
              fullWidth
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isSubmitting}
              required
              autoComplete="email"
              InputProps={{ startAdornment: <FieldIcon icon={<EmailOutlined />} active={Boolean(email)} /> }}
              sx={fieldSx}
            />

            <TextField
              label="Password"
              type={showPassword ? 'text' : 'password'}
              fullWidth
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isSubmitting}
              required
              autoComplete="new-password"
              InputProps={{
                startAdornment: <FieldIcon icon={<LockOutlined />} active={Boolean(password)} />,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword((v) => !v)} tabIndex={-1} edge="end" sx={{ color: '#94a3b8', '&:hover': { color: '#0a1628' } }}>
                      {showPassword ? <VisibilityOff sx={{ fontSize: 18 }} /> : <Visibility sx={{ fontSize: 18 }} />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={fieldSx}
            />

            <TextField
              label="Confirm password"
              type={showPassword ? 'text' : 'password'}
              fullWidth
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={isSubmitting}
              required
              autoComplete="new-password"
              error={Boolean(confirmPassword) && password !== confirmPassword}
              helperText={Boolean(confirmPassword) && password !== confirmPassword ? 'Passwords do not match.' : ''}
              InputProps={{ startAdornment: <FieldIcon icon={<LockOutlined />} active={Boolean(confirmPassword)} /> }}
              sx={fieldSx}
            />

            <Button type="submit" fullWidth variant="contained" disabled={!isValid || isSubmitting} sx={submitSx}>
              {isSubmitting ? <CircularProgress size={20} sx={{ color: 'rgba(255,255,255,0.8)' }} /> : 'Create account'}
            </Button>
          </Box>

          <Typography sx={{ mt: 3, color: '#64748b', fontSize: '0.86rem', textAlign: 'center' }}>
            Already have an account? <Link component={RouterLink} to="/login" sx={authLinkSx}>Sign in</Link>
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

const FieldIcon: React.FC<{ icon: React.ReactElement; active: boolean }> = ({ icon, active }) => (
  <InputAdornment position="start">
    {React.cloneElement(icon, { sx: { fontSize: 18, color: active ? '#0a1628' : '#cbd5e1' } })}
  </InputAdornment>
);

const authLinkSx = {
  fontSize: '0.86rem',
  color: '#2563eb',
  cursor: 'pointer',
  fontWeight: 700,
  textDecoration: 'none',
  '&:hover': { color: '#1d4ed8', textDecoration: 'underline' },
};

export default SignUpPage;
